/**
 * Plotea "Organizacja" settings (owner decision O4, self-service): members,
 * roles, removal, invitations (send, resend, revoke) for owners and admins of
 * the active organization; leaving for everyone. Reachable without a plan, so
 * an organization can be set up before it buys one.
 * Contract: docs/architecture/plotea.md ("Identity and roster").
 */
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  LogOut,
  Mail,
  RotateCw,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../lib/auth'
import { useOrganizations } from '../hooks/useOrganizations'
import { ApiError } from '../lib/errors'
import { qk } from '../lib/queryKeys'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderDescription,
  PageHeaderIcon,
  PageHeaderTitle,
} from '../components/Page'
import {
  changeMemberRole,
  deleteOrganization,
  getRoster,
  inviteToOrganization,
  leaveOrganization,
  removeOrganizationMember,
  resendInvitation,
  revokeInvitation,
  ROLE_LABEL,
  rosterErrorMessage,
  type PloteaRole,
  type Roster,
} from '../lib/endpoints/ploteaOrganization'

const ROLES: PloteaRole[] = ['owner', 'admin', 'member']

const SELECT_CLASS =
  'rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground disabled:opacity-50'

export const rosterKey = (id: string) => ['plotea', 'roster', id] as const

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** Roles a manager may assign: admins never grant or change `owner`. */
function assignableRoles(viewer: PloteaRole): PloteaRole[] {
  return viewer === 'owner' ? ROLES : ROLES.filter((role) => role !== 'owner')
}

export function PloteaOrganizationPage() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const activeId = user?.active_org?.id ?? ''
  const { query: orgs } = useOrganizations()
  const orgName =
    orgs.data?.find((org) => org.id === activeId)?.name ??
    user?.active_org?.slug ??
    ''

  const roster = useQuery({
    queryKey: rosterKey(activeId),
    queryFn: () => getRoster(activeId),
    enabled: activeId !== '',
    retry: (count, error) =>
      !(error instanceof ApiError && error.status === 403) && count < 2,
  })
  // Members (403) see the organization and can leave; nothing else.
  const isManager = roster.isSuccess

  return (
    <Page>
      <PageHeader>
        <PageHeaderIcon>
          <Building2 />
        </PageHeaderIcon>
        <PageHeaderTitle data-testid="plotea-org-title">
          {roster.data?.name || orgName || 'Organizacja'}
        </PageHeaderTitle>
        <PageHeaderDescription>
          Członkowie, zaproszenia i role w organizacji.
        </PageHeaderDescription>
      </PageHeader>
      <PageContent className="space-y-8">
        {activeId === '' && (
          <p className="text-sm text-muted-foreground">
            Nie wybrano organizacji.
          </p>
        )}
        {roster.isLoading && (
          <p className="text-sm text-muted-foreground">Wczytywanie…</p>
        )}
        {roster.isError && !isManager && (
          <p
            className="text-sm text-muted-foreground"
            data-testid="plotea-org-member-notice"
          >
            Członkami i zaproszeniami zarządzają właściciele i administratorzy
            organizacji.
          </p>
        )}
        {roster.data && (
          <ManageRoster
            roster={roster.data}
            organizationId={activeId}
            viewerId={user?.id ?? ''}
          />
        )}
        {activeId !== '' && (
          <LeaveSection organizationId={activeId} name={orgName} />
        )}
        {roster.data?.viewer_role === 'owner' && (
          <DeleteSection organizationId={activeId} name={roster.data.name} />
        )}
      </PageContent>
    </Page>
  )
}

function useRosterMutation<V>(
  organizationId: string,
  fn: (variables: V) => Promise<unknown>,
  success: string,
) {
  const qc = useQueryClient()
  return useMutation<unknown, Error, V>({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(success)
      void qc.invalidateQueries({ queryKey: rosterKey(organizationId) })
    },
    onError: (error) => toast.error(rosterErrorMessage(error)),
  })
}

function ManageRoster({
  roster,
  organizationId,
  viewerId,
}: {
  roster: Roster
  organizationId: string
  viewerId: string
}) {
  const viewer = roster.viewer_role
  const roles = assignableRoles(viewer)
  const owners = roster.members.filter((m) => m.role === 'owner').length

  const changeRole = useRosterMutation(
    organizationId,
    ({ userId, role }: { userId: string; role: PloteaRole }) =>
      changeMemberRole(organizationId, userId, role),
    'Rola zmieniona.',
  )
  const remove = useRosterMutation(
    organizationId,
    (userId: string) => removeOrganizationMember(organizationId, userId),
    'Usunięto z organizacji.',
  )
  const resend = useRosterMutation(
    organizationId,
    async (invitationId: string) => {
      const sent = await resendInvitation(organizationId, invitationId)
      if (!sent.email_sent) throw new Error('Nie udało się wysłać wiadomości.')
    },
    'Zaproszenie wysłane ponownie.',
  )
  const revoke = useRosterMutation(
    organizationId,
    (invitationId: string) => revokeInvitation(organizationId, invitationId),
    'Zaproszenie anulowane.',
  )
  const busy = changeRole.isPending || remove.isPending

  return (
    <>
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">Członkowie</h2>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm" data-testid="plotea-members">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <th className="p-3 font-medium">Osoba</th>
                <th className="p-3 font-medium">Rola</th>
                <th className="p-3 font-medium">Dołączył(a)</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {roster.members.map((member) => {
                const isSelf = member.user_id === viewerId
                // Admins never touch owners; the last owner stays an owner.
                const locked =
                  (member.role === 'owner' && viewer !== 'owner') ||
                  (member.role === 'owner' && owners <= 1)
                return (
                  <tr
                    key={member.user_id}
                    className="border-t border-border"
                    data-testid="plotea-member-row"
                  >
                    <td className="p-3">
                      <div className="font-medium text-foreground">
                        {member.name || member.email}
                        {isSelf && (
                          <span className="ml-1 text-muted-foreground">
                            (Ty)
                          </span>
                        )}
                      </div>
                      {member.name && (
                        <div className="text-muted-foreground">
                          {member.email}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {locked ? (
                        <span data-testid="plotea-member-role">
                          {ROLE_LABEL[member.role]}
                        </span>
                      ) : (
                        <select
                          aria-label={`Rola: ${member.email}`}
                          className={SELECT_CLASS}
                          data-testid="plotea-member-role-select"
                          value={member.role}
                          disabled={busy}
                          onChange={(event) =>
                            changeRole.mutate({
                              userId: member.user_id,
                              role: event.target.value as PloteaRole,
                            })
                          }
                        >
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABEL[role]}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {formatDate(member.joined_at)}
                    </td>
                    <td className="p-3 text-right">
                      {!isSelf &&
                        !(member.role === 'owner' && viewer !== 'owner') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Usuń ${member.email}`}
                            data-testid="plotea-member-remove"
                            disabled={busy}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Usunąć ${member.name || member.email} z organizacji?`,
                                )
                              )
                                remove.mutate(member.user_id)
                            }}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <InviteForm organizationId={organizationId} roles={roles} />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          Oczekujące zaproszenia
        </h2>
        {roster.invitations.length === 0 ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="plotea-invitations-empty"
          >
            Brak oczekujących zaproszeń.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {roster.invitations.map((invitation) => {
              const canManage =
                invitation.role !== 'owner' || viewer === 'owner'
              return (
                <li
                  key={invitation.id}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
                  data-testid="plotea-invitation-row"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-foreground">
                      {invitation.email}
                    </div>
                    <div className="text-muted-foreground">
                      {ROLE_LABEL[invitation.role]} ·{' '}
                      {invitation.status === 'expired'
                        ? 'wygasło'
                        : invitation.last_sent_at
                          ? `wysłano ${formatDate(invitation.last_sent_at)}`
                          : 'nie wysłano'}{' '}
                      · ważne do {formatDate(invitation.expires_at)}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="plotea-invitation-resend"
                        disabled={resend.isPending}
                        onClick={() => resend.mutate(invitation.id)}
                      >
                        <RotateCw className="size-4" />
                        Wyślij ponownie
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="plotea-invitation-revoke"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(invitation.id)}
                      >
                        <X className="size-4" />
                        Anuluj
                      </Button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </>
  )
}

function InviteForm({
  organizationId,
  roles,
}: {
  organizationId: string
  roles: PloteaRole[]
}) {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<PloteaRole>('member')
  const invite = useMutation({
    mutationFn: () => inviteToOrganization(organizationId, email.trim(), role),
    onSuccess: (sent) => {
      if (sent.email_sent)
        toast.success(`Wysłano zaproszenie na ${sent.invitation.email}.`)
      else
        toast.error(
          'Zaproszenie zapisane, ale nie udało się wysłać wiadomości. Spróbuj wysłać ponownie.',
        )
      setEmail('')
      setRole('member')
      void qc.invalidateQueries({ queryKey: rosterKey(organizationId) })
    },
    onError: (error) => toast.error(rosterErrorMessage(error)),
  })
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">Zaproś osobę</h2>
      <form
        className="flex flex-wrap items-end gap-3"
        data-testid="plotea-invite-form"
        onSubmit={(event) => {
          event.preventDefault()
          if (email.trim() !== '') invite.mutate()
        }}
      >
        <div className="min-w-56 flex-1 space-y-1.5">
          <Label htmlFor="plotea-invite-email">Email</Label>
          <Input
            id="plotea-invite-email"
            type="email"
            placeholder="email@example.com"
            data-testid="plotea-invite-email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="plotea-invite-role">Rola</Label>
          <select
            id="plotea-invite-role"
            className={`${SELECT_CLASS} block h-9`}
            data-testid="plotea-invite-role"
            value={role}
            onChange={(event) => setRole(event.target.value as PloteaRole)}
          >
            {roles.map((value) => (
              <option key={value} value={value}>
                {ROLE_LABEL[value]}
              </option>
            ))}
          </select>
        </div>
        <Button
          type="submit"
          data-testid="plotea-invite-submit"
          disabled={email.trim() === '' || invite.isPending}
        >
          <UserPlus />
          Zaproś
        </Button>
      </form>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Mail className="size-3.5" />
        Zaproszenie jest ważne 7 dni. Osoba bez konta założy je z linku.
      </p>
    </section>
  )
}

/** After leaving or deleting: switch to another organization or none. */
function useExitOrganization(organizationId: string) {
  const qc = useQueryClient()
  const nav = useNavigate()
  const { query: orgs, setActive, clearActive } = useOrganizations()
  return async () => {
    const next = orgs.data?.find((org) => org.id !== organizationId)
    try {
      if (next) await setActive.mutateAsync(next.id)
      else await clearActive.mutateAsync()
    } finally {
      void qc.invalidateQueries({ queryKey: qk.organizations() })
      void nav({ to: '/site', replace: true })
    }
  }
}

function LeaveSection({
  organizationId,
  name,
}: {
  organizationId: string
  name: string
}) {
  const exit = useExitOrganization(organizationId)
  const leave = useMutation({
    mutationFn: () => leaveOrganization(organizationId),
    onSuccess: async () => {
      toast.success('Opuściłeś organizację.')
      await exit()
    },
    onError: (error) => toast.error(rosterErrorMessage(error)),
  })
  return (
    <section className="space-y-2 border-t border-border pt-6">
      <h2 className="text-sm font-semibold text-foreground">
        Opuść organizację
      </h2>
      <p className="text-sm text-muted-foreground">
        Stracisz dostęp do analiz tej organizacji. Ostatni właściciel nie może
        jej opuścić.
      </p>
      <Button
        variant="outline"
        data-testid="plotea-org-leave"
        disabled={leave.isPending}
        onClick={() => {
          if (window.confirm(`Opuścić organizację ${name}?`)) leave.mutate()
        }}
      >
        <LogOut className="size-4" />
        Opuść organizację
      </Button>
    </section>
  )
}

function DeleteSection({
  organizationId,
  name,
}: {
  organizationId: string
  name: string
}) {
  const exit = useExitOrganization(organizationId)
  const [typed, setTyped] = useState('')
  const remove = useMutation({
    mutationFn: () => deleteOrganization(organizationId, typed.trim()),
    onSuccess: async () => {
      toast.success('Organizacja usunięta.')
      await exit()
    },
    onError: (error) => toast.error(rosterErrorMessage(error)),
  })
  const matches = typed.trim() === name.trim() && name.trim() !== ''
  return (
    <section
      className="space-y-3 rounded-md border border-destructive/40 p-4"
      data-testid="plotea-org-danger-zone"
    >
      <h2 className="text-sm font-semibold text-destructive">
        Usuń organizację
      </h2>
      <p className="text-sm text-muted-foreground">
        Wszyscy członkowie stracą dostęp, oczekujące zaproszenia zostaną
        anulowane, a ręcznie przyznany dostęp do planu wygaśnie. Analizy nie są
        kasowane, ale nie będą już dostępne. Tej operacji nie można cofnąć z
        poziomu aplikacji.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="plotea-org-delete-name">
          Wpisz nazwę organizacji ({name}), aby potwierdzić
        </Label>
        <Input
          id="plotea-org-delete-name"
          data-testid="plotea-org-delete-name"
          autoComplete="off"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>
      <Button
        variant="destructive"
        data-testid="plotea-org-delete"
        disabled={!matches || remove.isPending}
        onClick={() => remove.mutate()}
      >
        <Trash2 className="size-4" />
        Usuń organizację
      </Button>
    </section>
  )
}
